import dayjs from 'dayjs';
import { useWatch } from 'react-hook-form';

import { VideoPlayer } from '@/components/Video';
import { BroadcastScheduleProgramBodySegmentDetailCardBodySegmentTranscription } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentDetailCard/components/BroadcastScheduleProgramBodySegmentDetailCardBodySegmentTranscription';
import { useBroadcastScheduleProgramBodySegmentDetailCardBodySegmentNavigationState } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentDetailCard/hooks/useBroadcastScheduleProgramBodySegmentDetailCardBodySegmentNavigationState';
import { useBroadcastScheduleContext } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/context/BroadcastScheduleProvider';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { useGoNextOnEnded } from '@/features/broadcast-schedule-program/hooks/useGoNextOnEnded';

export function SegmentsDetailCardBody() {
  const {
    visibleBox,
    activeSegment,
    drawerLogo,
    segmentItems,
    videoPlayerRef,
    pendingSeekTime,
    videoControlRef,
    cutForm,
    presenceData,
    setPendingSeekTime,
    onDrawnRect,
    setVisibleBox,
    setPlayerVideoCurrentTime,
    setPlayerVideoDuration,
    setPlayerVideoIsPlaying,
  } = useBroadcastScheduleProgram();

  const { setAutoplayingClips, handleEndPresence, handleGoNextPresence, handleSeekPresence } =
    useBroadcastScheduleContext();

  const { handleNextItem, hasNextItem } =
    useBroadcastScheduleProgramBodySegmentDetailCardBodySegmentNavigationState({
      data: segmentItems,
    });

  const handleGoNext = useGoNextOnEnded({ hasNextItem, handleNextItem, videoControlRef });

  const handlePause = () => {
    setAutoplayingClips(false);
  };

  const startDatetime = useWatch({ control: cutForm.control, name: 'start_datetime' });
  const endDatetime = useWatch({ control: cutForm.control, name: 'end_datetime' });

  const markers = (() => {
    if (!activeSegment) return [];

    const segmentStart = dayjs(activeSegment.start_datetime);
    const segmentDuration = activeSegment.duration_in_seconds || 1;

    const result: { pos: number; type: 'start' | 'end' }[] = [];

    if (startDatetime) {
      const startOffset = dayjs(startDatetime).diff(segmentStart, 'second', true);
      if (startOffset >= 0 && startOffset <= segmentDuration) {
        result.push({ pos: startOffset / segmentDuration, type: 'start' });
      }
    }

    if (endDatetime) {
      const endOffset = dayjs(endDatetime).diff(segmentStart, 'second', true);
      if (endOffset >= 0 && endOffset <= segmentDuration) {
        result.push({ pos: endOffset / segmentDuration, type: 'end' });
      }
    }

    return result;
  })();

  return (
    <div className="flex flex-col gap-2 overflow-auto">
      <div className="flex justify-center">
        <VideoPlayer
          activeBox={visibleBox}
          checkProgress={handleGoNextPresence}
          controls={false}
          customControls
          drawerLogo={drawerLogo}
          presenceData={presenceData}
          markers={markers}
          onDrawnRect={onDrawnRect}
          onEnded={handleGoNext}
          onEndPresence={handleEndPresence}
          onPause={handlePause}
          pendingSeekTime={pendingSeekTime}
          playing={true}
          ref={videoPlayerRef}
          setActiveBox={setVisibleBox}
          setPendingSeekTime={setPendingSeekTime}
          setPlayerVideoCurrentTime={setPlayerVideoCurrentTime}
          setPlayerVideoDuration={setPlayerVideoDuration}
          setPlayerVideoIsPlaying={setPlayerVideoIsPlaying}
          url={activeSegment?.presigned_url || ''}
          checkSeek={handleSeekPresence}
          onRefReady={ref => {
            if (videoControlRef && 'current' in videoControlRef) {
              (videoControlRef as React.MutableRefObject<unknown>).current = ref;
            }
          }}
        />
      </div>
      <BroadcastScheduleProgramBodySegmentDetailCardBodySegmentTranscription />
    </div>
  );
}
